import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { trDisplay } from '../../i18n';
import { formatCurrency } from '../../utils/formatters';
import { SWRHelp } from './SWRHelp';

// Read the completed result only. Never reconstruct real income from nominal
// medians or from inflation assumptions in the current, possibly edited form.
export function getSWRRealWithdrawalMilestones(data, yearsToSimulate) {
  const rows = Array.isArray(data) ? data : [];
  const validYear = year => Number.isInteger(year) && year >= 1 && year <= 100;
  const finalYear = validYear(yearsToSimulate) ? yearsToSimulate
    : rows.reduce((last, row) => validYear(row?.year) ? Math.max(last, row.year) : last, 0);
  if (!finalYear) return [];

  const requested = [3, 5, 10, 15, 20];
  for (let year = 30; year < finalYear; year += 10) requested.push(year);
  const years = [...new Set([...requested.filter(year => year < finalYear), finalYear])];

  return years.map(year => {
    const annualReal = rows.find(row => row?.year === year)?.realWithdrawal;
    return {
      year,
      monthlyReal: typeof annualReal === 'number' && Number.isFinite(annualReal) && annualReal >= 0
        ? annualReal / 12 : null,
    };
  });
}

export default function SWRRealWithdrawalMilestones({ data, yearsToSimulate }) {
  useTranslation();
  const milestones = useMemo(() => getSWRRealWithdrawalMilestones(data, yearsToSimulate), [data, yearsToSimulate]);

  return (
    <div className="min-w-0 max-w-full flex-[2_1_max-content] rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="swr-real-withdrawal-milestones">
      <p className="flex items-center gap-2 text-xs text-slate-500">
        {trDisplay('Prelievo reale mediano · €/mese')}
        <SWRHelp topic="realWithdrawalMilestones" side="bottom" buttonTrigger
          triggerClassName="relative inline-flex h-4 w-4 shrink-0 items-center justify-center after:absolute after:-inset-3.5 after:content-['']"
          triggerLabel={trDisplay('Informazioni sui prelievi reali mediani')} />
      </p>
      {milestones.length ? (
        <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-2" aria-label={trDisplay('Prelievi mensili reali mediani per anno')}>
          {milestones.map(({ year, monthlyReal }) => (
            <div key={year} className="min-w-14" data-testid={`swr-real-withdrawal-year-${year}`}>
              <dt className="whitespace-nowrap text-xs text-slate-500">{trDisplay('Anno ')}{year}</dt>
              <dd className="mt-0.5 whitespace-nowrap text-base font-semibold tabular-nums text-slate-900">
                {monthlyReal === null ? '—' : formatCurrency(monthlyReal)}
              </dd>
            </div>
          ))}
        </dl>
      ) : <p className="mt-1 text-xs text-slate-500">{trDisplay('Dati dei prelievi non disponibili.')}</p>}
    </div>
  );
}
